/**
 * Every GET route the web reads: its path template (`$name` segments) and the schema each
 * screen parses the body with. A call site builds its URL with `routePath` and passes
 * `GET_ROUTES.<key>.schema` to `getParsed`, so a tool that walks this registry parses with the
 * exact schema object the screen does.
 *
 * The operator plane's routes sit in `OPS_GET_ROUTES` (`src/ops/api/getRoutes.ts`), which the ops
 * bundle can import without reaching this file's tenant schemas; `/control/me` is spread in from
 * `SESSION_GET_ROUTES` for the same reason.
 *
 * Several keys share one path where the screens parse it differently (the monitoring-profile
 * list, the tracked-account list): the registry is keyed by reader, not by URL.
 *
 * `spends: true` marks a route whose handler can call a paid provider. Its `free` names, per
 * path parameter (or `?query` parameter), what that value must satisfy for the call to cost
 * nothing.
 */

import { z } from 'zod';

import { free, spending, type GetRoute } from './routePath';

import {
  accountKpisSchema,
  accountPublicationsSchema,
  accountReachSeriesSchema,
  accountSentimentSchema,
  accountTopicsSchema,
  placeListSchema,
  tagListSchema,
  trackedAccountListSchema,
  trackingSchema,
} from '../../app/campaign/accounts/api/schemas';
import {
  adversariesReadingSchema,
  analysisScopeStateSchema,
  candidateSchema,
} from '../../app/campaign/adversaries/api/schemas';
import {
  agendaSchema,
  audioTracksSchema,
  collectionSummarySchema,
  failuresSchema,
} from '../../app/campaign/monitoring/api/schemas';
import {
  alliancesSchema,
  campaignDetailSchema,
  unrelatedPostsSchema,
} from '../../app/campaign/record/api/schemas';
import { structureOutSchema, teamTrackedAccountListSchema } from '../../app/campaign/teams/schemas';
import {
  candidatureScopeSchema,
  electionsSchema,
  registrationsSchema,
  tseOfficesSchema,
  tseUnitsSchema,
} from '../../app/campaign/tse/api/schemas';
import {
  accountsPageSchema,
  monitoringProfileListedSchema,
  monitoringProfileSchema,
  profileListSchema,
} from '../../app/control/api/profileSchemas';
import { burnRateSchema } from '../../app/control/api/schemas';
import {
  amplificationSchema,
  breakdownSchema,
  commentTermsSchema,
  commentsSchema,
  compositionSchema,
  digestSchema,
  localitiesSchema,
  overviewSchema,
  postMetricsSchema,
  postSchema,
  postsSchema,
  rankedPostsSchema,
  sentimentSchema,
  sentimentSeriesSchema,
  sharedMediaSchema,
  territorySchema,
  topicsSchema,
} from '../../app/data/api/schemas';
import { SESSION_GET_ROUTES } from '../../app/session/getRoutes';
import { myInvitationsSchema } from '../../app/tenant/accept/schemas';
import {
  campaignSummarySchema,
  creditsSchema,
  dataRetentionSchema,
  fundedWindowSchema,
  invitationsListSchema,
  itemsOf,
  membersListSchema,
} from '../../app/tenant/api/schemas';
import {
  capacityRequestsResponseSchema,
  capacitySchema,
  consumptionSchema,
} from '../../app/tenant/capacity/api/schemas';
import { ontologyResponseSchema } from '../../app/tenant/ontology/api/schemas';
import { tagVocabularyResponseSchema } from '../../app/tenant/tags/api/schemas';

export { routePath, type GetRoute } from './routePath';

const PHOTO_STORED = 'every TSE registration it names already has a stored photo, or it names none';

export const GET_ROUTES = {
  ...SESSION_GET_ROUTES,
  myInvitations: free('/invitations', myInvitationsSchema),

  credits: free('/tenants/$tenantId/credits', creditsSchema),
  creditsConsumption: free('/tenants/$tenantId/credits/consumption', consumptionSchema),
  creditRequests: free('/tenants/$tenantId/credit-requests', capacityRequestsResponseSchema),
  fundedWindow: free('/tenants/$tenantId/funded-window', fundedWindowSchema),
  capacity: free('/tenants/$tenantId/capacity', capacitySchema),
  dataRetention: free('/tenants/$tenantId/data-retention', dataRetentionSchema),
  tenantCampaigns: free('/tenants/$tenantId/campaigns', itemsOf(campaignSummarySchema)),
  tenantInvitations: free('/tenants/$tenantId/invitations', invitationsListSchema),
  tenantMembers: free('/tenants/$tenantId/members', membersListSchema),
  tenantOntology: free('/tenants/$tenantId/ontology', ontologyResponseSchema),

  monitoringProfilesPicker: free(
    '/tenants/$tenantId/monitoring-profiles',
    z.object({ items: z.array(monitoringProfileSchema) }),
  ),
  monitoringProfilesCatalogue: free(
    '/tenants/$tenantId/monitoring-profiles',
    z.object({ items: z.array(monitoringProfileListedSchema) }),
  ),
  monitoringProfilesEditor: free('/tenants/$tenantId/monitoring-profiles', profileListSchema),
  monitoringProfileAccounts: free(
    '/tenants/$tenantId/monitoring-profiles/$profileId/accounts',
    accountsPageSchema,
  ),
  places: free('/tenants/$tenantId/places', placeListSchema),
  tenantElections: free('/tenants/$tenantId/elections', electionsSchema),
  tseOffices: free('/tenants/$tenantId/tse/offices', tseOfficesSchema),
  tseUnits: free('/tenants/$tenantId/tse/units', tseUnitsSchema),
  tseCandidates: free('/tenants/$tenantId/tse/candidates', registrationsSchema),
  tseCandidateScope: free('/tenants/$tenantId/tse/candidates/scope', candidatureScopeSchema),

  campaignDetail: spending('/campaigns/$campaignId', campaignDetailSchema, {
    campaignId: `a campaign whose candidate has a stored photo, or none`,
  }),
  campaignAlliances: spending('/campaigns/$campaignId/alliances', alliancesSchema, {
    campaignId: `a campaign with ${PHOTO_STORED}`,
  }),
  allianceSuggestions: spending('/campaigns/$campaignId/alliances/suggestions', alliancesSchema, {
    campaignId: `a campaign whose suggestions have ${PHOTO_STORED}`,
  }),
  allianceOptions: spending('/campaigns/$campaignId/alliances/options', alliancesSchema, {
    '?q': 'a query matching no TSE registration, or only ones with a stored photo',
  }),
  adversariesReading: spending(
    '/campaigns/$campaignId/reading/adversaries',
    adversariesReadingSchema,
    {
      campaignId: `a campaign whose tracked accounts' ${PHOTO_STORED}`,
    },
  ),
  accountCandidate: spending(
    '/campaigns/$campaignId/tracked-accounts/$trackedAccountId/candidate',
    candidateSchema,
    {
      trackedAccountId:
        'a tracked account bound to no TSE registration, or one with a stored photo',
    },
  ),
  postDigest: spending('/read/campaigns/$campaignId/posts/$postId/digest', digestSchema, {
    postId: 'a post with no commenter rows, or whose digest is already in the LLM cache',
  }),

  campaignStructure: free('/campaigns/$campaignId/structure', structureOutSchema),
  campaignTags: free('/campaigns/$campaignId/tags', tagListSchema),
  campaignTagCategories: free(
    '/campaigns/$campaignId/tags/categories',
    tagVocabularyResponseSchema,
  ),
  campaignBurnRate: free('/campaigns/$campaignId/burn-rate', burnRateSchema),
  campaignAgenda: free('/campaigns/$campaignId/agenda', agendaSchema),
  campaignFailures: free('/campaigns/$campaignId/failures', failuresSchema),
  campaignAudio: free('/campaigns/$campaignId/audio', audioTracksSchema),
  campaignCollectionSummary: free(
    '/campaigns/$campaignId/collection-summary',
    collectionSummarySchema,
  ),
  unrelatedPosts: free('/campaigns/$campaignId/unrelated-posts', unrelatedPostsSchema),
  campaignBreakdown: free('/campaigns/$campaignId/breakdown', breakdownSchema),
  trackedAccounts: free('/campaigns/$campaignId/tracked-accounts', trackedAccountListSchema),
  teamTrackedAccounts: free(
    '/campaigns/$campaignId/tracked-accounts',
    teamTrackedAccountListSchema,
  ),
  trackedAccountTracking: free(
    '/campaigns/$campaignId/tracked-accounts/$trackedAccountId/tracking',
    trackingSchema,
  ),
  analysisScope: free(
    '/campaigns/$campaignId/tracked-accounts/$trackedAccountId/analysis-scope',
    analysisScopeStateSchema,
  ),
  accountKpis: free(
    '/campaigns/$campaignId/tracked-accounts/$trackedAccountId/reading/kpis',
    accountKpisSchema,
  ),
  accountReachSeries: free(
    '/campaigns/$campaignId/tracked-accounts/$trackedAccountId/reading/reach-series',
    accountReachSeriesSchema,
  ),
  accountSentiment: free(
    '/campaigns/$campaignId/tracked-accounts/$trackedAccountId/reading/sentiment',
    accountSentimentSchema,
  ),
  accountTopics: free(
    '/campaigns/$campaignId/tracked-accounts/$trackedAccountId/reading/topics',
    accountTopicsSchema,
  ),
  accountPublications: free(
    '/campaigns/$campaignId/tracked-accounts/$trackedAccountId/reading/publications',
    accountPublicationsSchema,
  ),

  readTerms: free('/read/campaigns/$campaignId/terms', commentTermsSchema),
  readTopics: free('/read/campaigns/$campaignId/topics', topicsSchema),
  readLocalities: free('/read/campaigns/$campaignId/localities', localitiesSchema),
  readRankedPosts: free('/read/campaigns/$campaignId/ranked-posts', rankedPostsSchema),
  readPosts: free('/read/campaigns/$campaignId/posts', postsSchema),
  readPost: free('/read/campaigns/$campaignId/posts/$postId', postSchema),
  postMetrics: free('/read/campaigns/$campaignId/posts/$postId/metrics', postMetricsSchema),
  postComposition: free('/read/campaigns/$campaignId/posts/$postId/composition', compositionSchema),
  postTerms: free('/read/campaigns/$campaignId/posts/$postId/terms', commentTermsSchema),
  postComments: free('/read/campaigns/$campaignId/posts/$postId/comments', commentsSchema),
  postSharedMedia: free(
    '/read/campaigns/$campaignId/posts/$postId/shared-media',
    sharedMediaSchema,
  ),
  readAmplification: free('/read/campaigns/$campaignId/amplification', amplificationSchema),
  readSentiment: free('/read/campaigns/$campaignId/sentiment', sentimentSchema),
  readSentimentSeries: free('/read/campaigns/$campaignId/sentiment', sentimentSeriesSchema),
  readTerritory: free('/read/campaigns/$campaignId/territory', territorySchema),
  readOverview: free('/read/campaigns/$campaignId/overview', overviewSchema),
} as const satisfies Record<string, GetRoute>;
